/**
 * Reference ids of the activity tracking (activity_feed). They must match:
 *   - backend/application/migrations/023_activity_seed_reference_data.sql (verbs, entities 1–38, modules 1–10)
 *   - backend/application/migrations/025_activity_entity_extensions.sql (entities 39–44)
 *   - 029_activity_entity_snapshot_calendar.sql (entity 45), 090_activity_entity_notice.sql (entity 46)
 *   - modules 11–13: 015_activity_module_all_companies.sql, 016_activity_verb_view_modules_contacts_lenders.sql
 */
export const ACTIVITY_VERB = {
  HAS_UPDATED: 1,
  HAS_DELETED: 2,
  HAS_CHANGED: 3,
  HAS_ADDED: 4,
  HAS_ASSIGNED: 5,
  HAS_SET: 6,
  HAS_DOWNLOADED: 7,
  CREATE: 8,
  ADD: 9,
  SAVE_AND_SEND: 10,
  EDIT: 11,
  DELETE: 12,
  SAVE: 13,
  VALIDATE: 14,
  DOWNLOAD: 15,
  UPDATE: 16,
  IMPORT: 17,
  VIEW: 18,
  REJECT: 19,
} as const;

export const ACTIVITY_ENTITY = {
  OPPORTUNITY: 1,
  DEAL: 2,
  APPOINTMENT: 3,
  CORPORATE: 4,
  CONTACT: 5,
  LENDER: 6,
  TRANCHE: 7,
  CLAUSE: 8,
  ROLE_AFS: 9,
  CONTACT_DEAL: 10,
  TRADE: 11,
  DRAW_DOWN: 12,
  MARGIN: 13,
  INTEREST: 14,
  DOCUMENT_KYC: 15,
  DOCUMENT_COMPANY: 16,
  DOCUMENT_LENDER: 17,
  ORGANIZATION_CONTACT: 18,
  TIMESHEET: 19,
  TIMESHEET_GROUP: 20,
  INVOICE: 21,
  FEE: 22,
  FIXED_FEE: 23,
  PRODUCT_FEE: 24,
  IBAN_JM: 25,
  USER: 26,
  ROLE: 27,
  ROLE_AFS_CONFIG: 28,
  EXCEL: 29,
  /** id 30 absent en base (saut après excel) — même ligne que « Overview » en 023 */
  BILLING_OVERVIEW: 31,
  INVOICE_TRACKING: 32,
  PENNYLANE_HISTORY: 33,
  KYC_LEGAL_DOCUMENTS: 34,
  KYC_ORGANIZATION: 35,
  KYC_CONTACTS_KYC: 36,
  KYC_HISTORY: 37,
  KYC_ACTIVITIES: 38,
  EXECUTIVE: 39,
  MANDAT: 40,
  STATUS: 41,
  CONTACT_ROLE: 42,
  CONFIG_API: 43,
  GEMINI_CHAT: 44,
  SNAPSHOT_CALENDAR: 45,
  NOTICE: 46,
} as const;

export const ACTIVITY_MODULE = {
  ADMIN: 1,
  CRM: 2,
  EXECUTION: 3,
  KYC: 4,
  KYC_COMPANY: 5,
  KYC_LENDER: 6,
  BILLING: 7,
  PAYMENT: 8,
  CONTACT: 9,
  KPI: 10,
  ALL_COMPANIES: 11,
  ALL_CONTACTS: 12,
  ALL_LENDERS: 13,
} as const;

/** Libellés = colonne label en base (activity_verb), ordre identique à 023 */
export const ACTIVITY_VERB_LABELS: Record<number, string> = {
  1: 'Has updated',
  2: 'Has deleted',
  3: 'Has changed',
  4: 'Has added',
  5: 'Has assigned',
  6: 'Has set',
  7: 'Has downloaded',
  8: 'Create',
  9: 'Add',
  10: 'Save and send',
  11: 'Edit',
  12: 'Delete',
  13: 'Save',
  14: 'Validate',
  15: 'Download',
  16: 'Update',
  17: 'Import',
  18: 'View',
  19: 'Reject',
};

/** Libellés = colonne label en base (activity_entity), ordre identique à 023 + 025 */
export const ACTIVITY_ENTITY_LABELS: Record<number, string> = {
  1: 'opportunity',
  2: 'deal',
  3: 'appointment',
  4: 'corporate',
  5: 'contact',
  6: 'lender',
  7: 'tranche',
  8: 'clause',
  9: 'role_afs',
  10: 'contact_deal',
  11: 'trade',
  12: 'draw_down',
  13: 'margin',
  14: 'interest',
  15: 'document_kyc',
  16: 'document_company',
  17: 'document_lender',
  18: 'organization_contact',
  19: 'timesheet',
  20: 'timesheet_group',
  21: 'invoice',
  22: 'fee',
  23: 'fixed_fee',
  24: 'product_fee',
  25: 'iban_jm',
  26: 'user',
  27: 'role_user',
  28: 'role_afs_config',
  29: 'excel',
  31: 'Overview',
  32: 'Invoice tracking',
  33: 'Pennylane history',
  34: 'Legal documents',
  35: 'Organization',
  36: 'Contacts KYC',
  37: 'KYC History',
  38: 'Activities',
  39: 'executive',
  40: 'mandat',
  41: 'status',
  42: 'contact_role',
  43: 'config_api',
  44: 'gemini_chat',
  45: 'snapshot_calendar',
  46: 'notice',
};

/** Libellés = colonne label en base (activity_module) — 023 + scripts 015/016 */
export const ACTIVITY_MODULE_LABELS: Record<number, string> = {
  1: 'admin',
  2: 'crm',
  3: 'execution',
  4: 'kyc',
  5: 'kyc_company',
  6: 'kyc_lender',
  7: 'billing',
  8: 'payment',
  9: 'contact',
  10: 'kpi',
  11: 'all_companies',
  12: 'all_contacts',
  13: 'all_lenders',
};

export function getVerbLabel(id: number): string {
  return ACTIVITY_VERB_LABELS[id] ?? '';
}
export function getEntityLabel(id: number): string {
  return ACTIVITY_ENTITY_LABELS[id] ?? '';
}
export function getModuleLabel(id: number): string {
  return ACTIVITY_MODULE_LABELS[id] ?? '';
}
